// client/src/components/pages/LexiStar/Replay/trips/TripsDayList.tsx (moved from Geofencing/trips, ST-00318)
//
// The Trips tab's day list (ST-00159): the day's trips grouped by vehicle (collapsible),
// one row per trip with its number and colour, times, from → to, distance, duration, top
// speed and stops on the way. Pointing at (or keyboard-focusing) a row highlights that trip
// on the map; pointing at a line on the map highlights its row here. A checkbox per trip
// and per vehicle switches them on the map, with Show all / Hide all.
//
// ST-00318: clicking a row replays the trip in Replay (`onSelect`); without Replay's
// permission it selects the trip instead. The selected trip (?trip=, also the one a replay
// opened from here goes back to) is marked and brought into view.
import { memo, useEffect, useRef, useState } from 'react';
import { FaChevronDown, FaChevronRight } from 'react-icons/fa';
import { formatMiningDateShort, formatMiningTime } from '../../../../../utils/dateUtils';
import {
  type Basemap,
  type DayTrip,
  type DayVehicle,
  type TripVisibility,
  type TripsDay,
  formatDuration,
  fromTo,
  inkOn,
  isTripVisible,
  stopsLabel,
  tripColor,
  vehicleVisibility,
} from './tripsDay';

/** The trip's number on its colour: the list's half of the number + colour pairing. */
export function TripSwatch({
  trip,
  basemap,
  hollow = false,
  className = '',
}: {
  trip: Pick<DayTrip, 'n'>;
  basemap: Basemap;
  hollow?: boolean;
  className?: string;
}) {
  const color = tripColor(trip.n, basemap);
  return (
    <span
      aria-hidden="true"
      className={`inline-flex items-center justify-center min-w-6 h-6 px-1.5 rounded-full text-[11px] font-bold tabular-nums flex-shrink-0 ${
        hollow ? 'text-slate-500' : 'ring-1 ring-slate-300'
      } ${className}`}
      // Hidden from the map: the same colour as a ring only (the map shows nothing).
      style={
        hollow
          ? { boxShadow: `inset 0 0 0 2px ${color}` }
          : { background: color, color: inkOn(color) }
      }
    >
      {trip.n}
    </span>
  );
}

export interface TripsDayListProps {
  day: TripsDay;
  visibility: TripVisibility;
  basemap: Basemap;
  multiDay: boolean;
  /** The trip highlighted on the map (by either side). */
  highlightKey: string | null;
  /** The highlight came from the map: bring its row into view. */
  highlightFromMap: boolean;
  /** The trip selected (?trip=): marked, and brought into view when it changes. */
  selectedKey: string | null;
  /** What a row's click does: replay the trip in Replay, or (without its permission) select it. */
  rowAction: 'replay' | 'select';
  onHighlight: (key: string | null) => void;
  onSelect: (key: string) => void;
  onToggleTrip: (trip: DayTrip, vehicle: DayVehicle) => void;
  onToggleVehicle: (vehicle: DayVehicle) => void;
  onShowAll: () => void;
  onHideAll: () => void;
}

function VehicleCheckbox({
  vehicle,
  visibility,
  onToggle,
}: {
  vehicle: DayVehicle;
  visibility: TripVisibility;
  onToggle: () => void;
}) {
  const ref = useRef<HTMLInputElement>(null);
  const state = vehicleVisibility(vehicle, visibility);
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = state === 'some';
  }, [state]);
  return (
    <input
      ref={ref}
      type="checkbox"
      checked={state === 'all'}
      onChange={onToggle}
      aria-label={`Show ${vehicle.regNo}'s trips on the map`}
      className="w-4 h-4 accent-teal-600 cursor-pointer flex-shrink-0"
    />
  );
}

function TripsDayList({
  day,
  visibility,
  basemap,
  multiDay,
  highlightKey,
  highlightFromMap,
  selectedKey,
  rowAction,
  onHighlight,
  onSelect,
  onToggleTrip,
  onToggleVehicle,
  onShowAll,
  onHideAll,
}: TripsDayListProps) {
  const [collapsed, setCollapsed] = useState<ReadonlySet<string>>(() => new Set());
  const rowRefs = useRef(new Map<string, HTMLLIElement>());

  // A line pointed at on the map: bring its row into view (never while the list is in use).
  useEffect(() => {
    if (!highlightFromMap || !highlightKey) return;
    rowRefs.current.get(highlightKey)?.scrollIntoView?.({ block: 'nearest' });
  }, [highlightKey, highlightFromMap]);
  // The selected trip (a link, or Back from its replay): bring its row into view.
  useEffect(() => {
    if (selectedKey) rowRefs.current.get(selectedKey)?.scrollIntoView?.({ block: 'nearest' });
  }, [selectedKey]);

  const toggleCollapsed = (regNo: string) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(regNo)) next.delete(regNo);
      else next.add(regNo);
      return next;
    });

  const shownCount = day.trips.filter((t) => isTripVisible(t, visibility)).length;
  const highlightedVehicle = highlightKey ? day.byKey.get(highlightKey)?.regNo : undefined;

  return (
    <div className="space-y-2">
      {/* Summary + show / hide all */}
      <div className="bg-surface rounded-lg border border-slate-200 px-3 py-2 flex flex-wrap items-center justify-between gap-x-3 gap-y-1.5">
        <p className="text-xs font-medium text-slate-600 tabular-nums">
          {day.trips.length} trip{day.trips.length === 1 ? '' : 's'}
          {' · '}
          {day.totalKm.toFixed(1)} km
          {' · '}
          {formatDuration(day.totalMin)} driving
          {shownCount !== day.trips.length && (
            <span className="text-slate-400"> · {shownCount} on the map</span>
          )}
        </p>
        <div className="flex items-center gap-1" role="group" aria-label="Show or hide every trip">
          <button
            type="button"
            onClick={onShowAll}
            disabled={shownCount === day.trips.length}
            className="px-2 py-1 text-xs font-semibold rounded-md text-teal-700 enabled:hover:bg-teal-50 disabled:opacity-40 disabled:cursor-default focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
          >
            Show all
          </button>
          <button
            type="button"
            onClick={onHideAll}
            disabled={shownCount === 0}
            className="px-2 py-1 text-xs font-semibold rounded-md text-teal-700 enabled:hover:bg-teal-50 disabled:opacity-40 disabled:cursor-default focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
          >
            Hide all
          </button>
        </div>
      </div>

      <div className="space-y-2 lg:max-h-[600px] lg:overflow-y-auto lg:pr-1">
        {day.vehicles.map((vehicle) => {
          const isCollapsed = collapsed.has(vehicle.regNo);
          const groupId = `trips-group-${vehicle.regNo.replace(/[^A-Za-z0-9_-]/g, '_')}`;
          return (
            <section
              key={vehicle.regNo}
              aria-label={`${vehicle.regNo} trips`}
              className={`bg-surface rounded-lg border ${
                isCollapsed && highlightedVehicle === vehicle.regNo
                  ? 'border-teal-500 ring-1 ring-teal-500'
                  : 'border-slate-200'
              }`}
            >
              <div className="flex items-center gap-2 px-3 py-2">
                <VehicleCheckbox
                  vehicle={vehicle}
                  visibility={visibility}
                  onToggle={() => onToggleVehicle(vehicle)}
                />
                <button
                  type="button"
                  onClick={() => toggleCollapsed(vehicle.regNo)}
                  aria-expanded={!isCollapsed}
                  aria-controls={groupId}
                  className="flex-1 min-w-0 flex items-center gap-2 text-left rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
                >
                  {isCollapsed ? (
                    <FaChevronRight
                      className="text-slate-400 flex-shrink-0"
                      size={11}
                      aria-hidden="true"
                    />
                  ) : (
                    <FaChevronDown
                      className="text-slate-400 flex-shrink-0"
                      size={11}
                      aria-hidden="true"
                    />
                  )}
                  <span className="text-sm font-semibold text-slate-800 truncate">
                    {vehicle.regNo}
                  </span>
                  <span className="ml-auto text-xs text-slate-500 whitespace-nowrap tabular-nums">
                    {vehicle.trips.length} trip{vehicle.trips.length === 1 ? '' : 's'} ·{' '}
                    {vehicle.distanceKm.toFixed(1)} km
                  </span>
                </button>
              </div>

              {!isCollapsed && (
                <ul id={groupId} className="border-t border-slate-100 divide-y divide-slate-100">
                  {vehicle.trips.length === 0 && (
                    <li className="px-3 py-2.5 text-xs text-slate-500">
                      No trips: parked or standing for the whole {multiDay ? 'period' : 'day'}.
                    </li>
                  )}
                  {vehicle.trips.map((trip) => {
                    const visible = isTripVisible(trip, visibility);
                    const highlighted = highlightKey === trip.key;
                    const selected = selectedKey === trip.key;
                    return (
                      <li
                        key={trip.key}
                        ref={(el) => {
                          if (el) rowRefs.current.set(trip.key, el);
                          else rowRefs.current.delete(trip.key);
                        }}
                        data-trip-key={trip.key}
                        onMouseEnter={() => onHighlight(trip.key)}
                        onMouseLeave={() => onHighlight(null)}
                        onFocus={() => onHighlight(trip.key)}
                        onBlur={(e) => {
                          if (!e.currentTarget.contains(e.relatedTarget as Node | null))
                            onHighlight(null);
                        }}
                        className={`flex items-start gap-2.5 px-3 py-2.5 transition-colors ${
                          highlighted
                            ? 'bg-teal-50 shadow-[inset_3px_0_0_0_rgb(var(--tw-teal-500))]'
                            : ''
                        } ${selected ? 'ring-1 ring-inset ring-teal-500' : ''} ${visible ? '' : 'opacity-60'}`}
                      >
                        <input
                          type="checkbox"
                          checked={visible}
                          onChange={() => onToggleTrip(trip, vehicle)}
                          aria-label={`Show trip ${trip.n} on the map`}
                          className="mt-1 w-4 h-4 accent-teal-600 cursor-pointer flex-shrink-0"
                        />
                        <button
                          type="button"
                          onClick={() => onSelect(trip.key)}
                          aria-current={selected ? 'true' : undefined}
                          aria-label={`Trip ${trip.n}, ${vehicle.regNo}, ${formatMiningTime(trip.startTs)} to ${formatMiningTime(trip.endTs)}, ${fromTo(trip)}, ${(trip.distanceKm || 0).toFixed(1)} km. ${rowAction === 'replay' ? 'Replay the trip' : 'Select the trip'}`}
                          className={`flex-1 min-w-0 text-left rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 ${
                            highlighted ? '' : 'hover:text-slate-900'
                          }`}
                        >
                          <span className="flex items-center gap-2 min-w-0">
                            <TripSwatch trip={trip} basemap={basemap} hollow={!visible} />
                            <span className="text-sm font-semibold text-slate-800 truncate tabular-nums">
                              {multiDay && (
                                <span className="text-slate-400 font-normal mr-1">
                                  {formatMiningDateShort(trip.startTs)}
                                </span>
                              )}
                              {formatMiningTime(trip.startTs)} – {formatMiningTime(trip.endTs)}
                            </span>
                            <span className="ml-auto text-xs text-slate-500 flex-shrink-0 tabular-nums">
                              {formatDuration(trip.durationMin)}
                            </span>
                          </span>
                          <span
                            className="block mt-1 text-xs text-slate-600 truncate"
                            title={fromTo(trip)}
                          >
                            {fromTo(trip)}
                          </span>
                          <span className="flex flex-wrap items-center gap-1.5 mt-1.5">
                            <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full whitespace-nowrap bg-teal-100 text-teal-700 tabular-nums">
                              {(trip.distanceKm || 0).toFixed(1)} km
                            </span>
                            <span
                              className={`inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full whitespace-nowrap tabular-nums ${
                                (trip.maxSpeedKmh || 0) > 120
                                  ? 'bg-red-100 text-red-700'
                                  : 'bg-slate-100 text-slate-600'
                              }`}
                            >
                              max {Math.round(trip.maxSpeedKmh || 0)} km/h
                            </span>
                            <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full whitespace-nowrap bg-slate-100 text-slate-600">
                              {stopsLabel(trip.stopCount)}
                            </span>
                          </span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </section>
          );
        })}
      </div>
    </div>
  );
}

export default memo(TripsDayList);
